/**
 * Copyright (c) 2022–2026 Podstack.ai
 * All rights reserved.
 * Proprietary and Confidential.
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 * This file is part of the Podstack Platform.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ServerStackIcon,
  CpuChipIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import CreateSSHKeyModal from '../components/pods/CreateSSHKeyModal';
import ProjectModal from '../components/projects/ProjectModal';
import {
  pricingService,
  sshKeyService,
} from '../services/api';
import { vmApi, vmPoolApi, cloudInitTemplateApi, dataVolumeApi } from '../services/vmApi';
import toast from 'react-hot-toast';
import { VMCreateRequest, VMCostEstimate, OSImage, VMBillingPeriod, VMRegionInfo, VMDiskCreate, CloudInitTemplate } from '../types/vm';
import { VMPoolCreate } from '../types/vmPool';
import { GPUPricing, GPUAvailability, SSHKey } from '../types';
import InsufficientBalanceModal from '../components/wallet/InsufficientBalanceModal';
import { useWallet } from '../hooks/useWallet';
import { useProjects } from '../hooks/useProjects';
import { SHOW_VM_COMING_SOON, VM_SHOW_GPU_CONFIGURATION } from '../config/vmPortalFeatures';

const billingPeriods: { value: VMBillingPeriod; label: string }[] = [
  { value: 'month', label: 'Per Month' },
  { value: 'quarter', label: 'Per Quarter' },
  { value: 'annual', label: 'Per Year' },
];

type Step = 'project' | 'pool' | 'region' | 'use_case' | 'resources' | 'os' | 'ssh' | 'advanced' | 'disks' | 'review';

const CreateVMPool: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  
  const [step, setStep] = useState<Step>(projectIdFromUrl ? 'pool' : 'project');
  const [selectedGPU, setSelectedGPU] = useState<GPUPricing | null>(null);
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');
  const [showCreateSSHKeyModal, setShowCreateSSHKeyModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [costEstimate, setCostEstimate] = useState<VMCostEstimate | null>(null);
  const [isEstimatingCost, setIsEstimatingCost] = useState(false);
  
  // Project selector state
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  
  // SSH Key selector state
  const [sshKeySearchQuery, setSshKeySearchQuery] = useState('');
  const [isSshKeyDropdownOpen, setIsSshKeyDropdownOpen] = useState(false);
  const sshKeyDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setIsProjectDropdownOpen(false);
      }
      if (sshKeyDropdownRef.current && !sshKeyDropdownRef.current.contains(event.target as Node)) {
        setIsSshKeyDropdownOpen(false);
      }
    };

    if (isProjectDropdownOpen || isSshKeyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProjectDropdownOpen, isSshKeyDropdownOpen]);

  // Wallet data for balance check
  const { availableBalance } = useWallet();

  const [gpuSearchQuery, setGpuSearchQuery] = useState('');
  const [gpuCurrentPage, setGpuCurrentPage] = useState(1);
  const gpusPerPage = 10;

  // Pool-specific fields
  const [poolName, setPoolName] = useState('');
  const [poolDescription, setPoolDescription] = useState('');
  const [replicas, setReplicas] = useState<number>(2);
  const [replicasInputValue, setReplicasInputValue] = useState<string>('2');

  // VM template fields (same as CreateVM)
  const [formData, setFormData] = useState<VMCreateRequest>({
    name: '', // This will be auto-generated for each VM in the pool
    description: '',
    cpu_cores: 2,
    memory_gb: 4,
    storage_gb: 50,
    gpu_count: 0,
    gpu_type: '',
    os_image: 'ubuntu:22.04',
    ssh_key_id: undefined,
    ssh_password: undefined,
    project_id: projectIdFromUrl || undefined,
    billing_period: 'month',
    region: 'india',
    use_dynamic_ssh_injection: true,
    use_case: 'development_testing',
    is_spot: false,
  });
  
  // Track authentication method selection
  const [authMethod, setAuthMethod] = useState<'ssh_key' | 'password' | 'none'>('none');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  
  // Advanced configuration state
  const [userDataScript, setUserDataScript] = useState<string>('');
  const [selectedCloudInitTemplate, setSelectedCloudInitTemplate] = useState<CloudInitTemplate | null>(null);
  const [additionalDisks, setAdditionalDisks] = useState<VMDiskCreate[]>([]);

  // Local state for input values to allow empty during typing
  const [cpuInputValue, setCpuInputValue] = useState<string>('2');
  const [memoryInputValue, setMemoryInputValue] = useState<string>('4');
  const [storageInputValue, setStorageInputValue] = useState<string>('50');
  const [gpuCountInputValue, setGpuCountInputValue] = useState<string>('0');

  const quickTemplates = [
    { name: 'Small', cpu: 1, memory: 2, storage: 20, description: 'Good for small projects, testing and light workloads' },
    { name: 'Medium', cpu: 2, memory: 4, storage: 50, description: 'Ideal for medium projects, web apps and moderate workloads' },
    { name: 'Large', cpu: 4, memory: 8, storage: 100, description: 'Perfect for large projects, databases and heavy workloads' },
    { name: 'XLarge', cpu: 8, memory: 16, storage: 200, description: 'Best for enterprise applications and high-performance workloads' },
  ];
  const computeClasses = [
    { id: 'balanced', name: 'Balanced', cpu: 2, memory: 4, storage: 50, description: 'Best default for most application workloads' },
    { id: 'cpu_intensive', name: 'CPU Intensive', cpu: 4, memory: 4, storage: 50, description: 'Best for compilers, build pipelines, and compute-heavy tasks' },
    { id: 'memory_intensive', name: 'Memory Intensive', cpu: 2, memory: 8, storage: 50, description: 'Best for analytics, cache layers, and memory-heavy services' },
  ] as const;
  const classConfigurations = {
    balanced: [
      { label: 'Balanced - Starter (2 vCPU, 4 GB)', cpu: 2, memory: 4 },
      { label: 'Balanced - Growth (4 vCPU, 8 GB)', cpu: 4, memory: 8 },
      { label: 'Balanced - Pro (8 vCPU, 16 GB)', cpu: 8, memory: 16 },
    ],
    cpu_intensive: [
      { label: 'CPU - Build (4 vCPU, 4 GB)', cpu: 4, memory: 4 },
      { label: 'CPU - Compute (8 vCPU, 8 GB)', cpu: 8, memory: 8 },
      { label: 'CPU - High Compute (16 vCPU, 16 GB)', cpu: 16, memory: 16 },
    ],
    memory_intensive: [
      { label: 'Memory - Cache (2 vCPU, 8 GB)', cpu: 2, memory: 8 },
      { label: 'Memory - Analytics (4 vCPU, 16 GB)', cpu: 4, memory: 16 },
      { label: 'Memory - Data Heavy (8 vCPU, 32 GB)', cpu: 8, memory: 32 },
    ],
  } as const;
  type ClassConfigOption = { label: string; cpu: number; memory: number };
  const [selectedComputeClass, setSelectedComputeClass] = useState<string>('balanced');
  const [selectedClassConfiguration, setSelectedClassConfiguration] = useState<string>('');
  const ENABLE_MANUAL_RESOURCE_OVERRIDE = false;

  const { projects, createProject, refetch: refetchProjects } = useProjects();

  const { data: gpuPricingData, isLoading: loadingGPU } = useQuery({
    queryKey: ['gpu-pricing'],
    queryFn: () => pricingService.getGPUPricing().then((res) => res.data),
    enabled: VM_SHOW_GPU_CONFIGURATION,
  });

  const { data: gpuAvailabilityData } = useQuery({
    queryKey: ['gpu-availability'],
    queryFn: () => pricingService.getGPUAvailability().then((res) => res.data),
    enabled: VM_SHOW_GPU_CONFIGURATION,
  });

  const { data: osImagesData, isLoading: loadingOSImages } = useQuery({
    queryKey: ['os-images'],
    queryFn: () => vmApi.listOSImages(undefined, true),
    staleTime: 300000,
  });

  const { data: sshKeysData, refetch: refetchSSHKeys } = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => sshKeyService.list().then((res) => res.data),
    enabled: step === 'ssh' || step === 'review' || step === 'advanced',
  });

  const sshKeys: SSHKey[] = Array.isArray(sshKeysData)
    ? sshKeysData
    : sshKeysData?.data || sshKeysData?.keys || [];

  // Fetch cloud-init templates
  const { data: cloudInitTemplatesData } = useQuery({
    queryKey: ['cloudinit-templates', formData.project_id],
    queryFn: () => cloudInitTemplateApi.listTemplates({ 
      project_id: formData.project_id,
      include_public: true 
    }),
    enabled: (step === 'advanced' || step === 'review') && !!formData.project_id,
    staleTime: 30000,
  });

  const cloudInitTemplates: CloudInitTemplate[] = cloudInitTemplatesData?.templates || [];

  // Fetch data volumes for existing_dv disk type
  const { data: dataVolumesData } = useQuery({
    queryKey: ['data-volumes', formData.project_id],
    queryFn: () => dataVolumeApi.listVolumes({ project_id: formData.project_id }),
    enabled: (step === 'disks' || step === 'review') && !!formData.project_id,
    staleTime: 30000,
  });

  const dataVolumes = dataVolumesData?.volumes || dataVolumesData?.data || [];

  const osImages: OSImage[] = useMemo(() => osImagesData?.images || [], [osImagesData?.images]);
  const osImagesByCategory = useMemo(() => {
    const list = SHOW_VM_COMING_SOON
      ? osImages
      : osImages.filter((img) => img.is_available);
    return list.reduce((acc, img) => {
      const cat = img.category;
      if (!acc[cat]) {
        acc[cat] = [];
      }
      acc[cat].push(img);
      return acc;
    }, {} as Record<string, OSImage[]>);
  }, [osImages]);

  // Helper to check GPU availability by type
  const getGPUAvailability = (gpuType: string): GPUAvailability | undefined => {
    const availabilityList = Array.isArray(gpuAvailabilityData) ? gpuAvailabilityData : gpuAvailabilityData?.data || [];
    return availabilityList.find((a: GPUAvailability) => a.gpu_type === gpuType);
  };

  // Sync input values when formData changes externally (e.g., from templates)
  useEffect(() => {
    setCpuInputValue(formData.cpu_cores.toString());
    setMemoryInputValue(formData.memory_gb.toString());
    setStorageInputValue(formData.storage_gb.toString());
    setGpuCountInputValue((formData.gpu_count || 0).toString());
  }, [formData.cpu_cores, formData.memory_gb, formData.storage_gb, formData.gpu_count]);

  // Estimate cost when form data changes (multiply by replicas for pool)
  useEffect(() => {
    const estimateCost = async () => {
      if (!formData.project_id || formData.cpu_cores < 1 || formData.memory_gb < 0.5 || formData.storage_gb < 10) {
        setCostEstimate(null);
        return;
      }

      setIsEstimatingCost(true);
      try {
        const estimate = await vmApi.estimateVMCost({
          cpu_cores: formData.cpu_cores,
          memory_gb: formData.memory_gb,
          storage_gb: formData.storage_gb,
          gpu_count: formData.gpu_count || 0,
          gpu_type: formData.gpu_type || undefined,
          billing_period: formData.billing_period || 'month',
          use_case: formData.use_case || 'development_testing',
          is_spot: formData.is_spot || false,
        });
        setCostEstimate(estimate);
      } catch (error) {
        console.error('Failed to estimate cost:', error);
      } finally {
        setIsEstimatingCost(false);
      }
    };

    // Debounce cost estimation
    const timeoutId = setTimeout(estimateCost, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.cpu_cores, formData.memory_gb, formData.storage_gb, formData.gpu_count, formData.gpu_type, formData.billing_period, formData.project_id, formData.use_case, formData.is_spot]);

  const handleSelectGPU = (gpu: GPUPricing | null) => {
    setSelectedGPU(gpu);
    if (gpu) {
      setFormData({
        ...formData,
        gpu_count: 1,
        gpu_type: gpu.gpu_type,
      });
      setGpuCountInputValue('1');
    } else {
      setFormData({
        ...formData,
        gpu_count: 0,
        gpu_type: '',
      });
      setGpuCountInputValue('0');
    }
  };

  const allGpus = Array.isArray(gpuPricingData) ? gpuPricingData : gpuPricingData?.data || [];
  const filteredGpus = allGpus.filter((gpu: GPUPricing) => {
    if (!getGPUAvailability(gpu.gpu_type)?.available_gpus) {
      return false;
    }
    if (gpuSearchQuery) {
      return gpu.gpu_type.toLowerCase().includes(gpuSearchQuery.toLowerCase());
    }
    return true;
  });

  const totalPages = Math.ceil(filteredGpus.length / gpusPerPage);
  const startIndex = (gpuCurrentPage - 1) * gpusPerPage;
  const endIndex = startIndex + gpusPerPage;
  const paginatedGpus = filteredGpus.slice(startIndex, endIndex);

  // Define available regions
  const regions: VMRegionInfo[] = [
    {
      id: 'india',
      name: 'India',
      displayName: 'Jamshedpur, India',
      location: 'Jamshedpur',
      flag: '🇮🇳',
      isAvailable: true,
      description: 'Low latency for Indian users, data residency compliance',
    },
    {
      id: 'us-east',
      name: 'US East',
      displayName: 'Virginia, USA',
      location: 'Virginia',
      flag: '🇺🇸',
      isAvailable: false,
      description: 'Coming soon - East Coast United States',
    },
    {
      id: 'us-west',
      name: 'US West',
      displayName: 'California, USA',
      location: 'California',
      flag: '🇺🇸',
      isAvailable: false,
      description: 'Coming soon - West Coast United States',
    },
    {
      id: 'europe',
      name: 'Europe',
      displayName: 'Frankfurt, Germany',
      location: 'Frankfurt',
      flag: '🇪🇺',
      isAvailable: false,
      description: 'Coming soon - Central Europe, GDPR compliant',
    },
    {
      id: 'asia-pacific',
      name: 'Asia Pacific',
      displayName: 'Singapore',
      location: 'Singapore',
      flag: '🇸🇬',
      isAvailable: false,
      description: 'Coming soon - Southeast Asia, low latency',
    },
  ];

  const regionsForSelection = SHOW_VM_COMING_SOON
    ? regions
    : regions.filter((r) => r.isAvailable);

  const createMutation = useMutation({
    mutationFn: async (poolData: VMPoolCreate) => {
      return vmPoolApi.createPool(poolData, poolData.project_id);
    },
    onSuccess: () => {
      toast.success('VM Pool creation initiated');
      navigate('/vm-pools');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to create VM Pool';
      toast.error(errorMessage);
    },
  });

  const handleCreate = async () => {
    // Validate pool name
    const poolNameError = validatePoolName(poolName);
    if (poolNameError) {
      setErrors({ poolName: poolNameError });
      toast.error(poolNameError);
      return;
    }
    setErrors({});

    if (replicas < 1 || replicas > 100) {
      toast.error('Replicas must be between 1 and 100');
      return;
    }

    if (!formData.project_id) {
      toast.error('Project is required');
      return;
    }

    // Validate VM template
    if (formData.cpu_cores < 1) {
      toast.error('CPU cores must be at least 1');
      return;
    }

    if (formData.memory_gb < 0.5) {
      toast.error('Memory must be at least 0.5 GB');
      return;
    }

    if (formData.storage_gb < 10) {
      toast.error('Storage must be at least 10 GB');
      return;
    }

    // Check balance if cost estimate is available
    if (costEstimate && availableBalance !== null) {
      const totalCost = costEstimate.period_cost * replicas;
      if (availableBalance < totalCost) {
        setBalanceErrorMessage(
          `Insufficient balance. Required: ₹${totalCost.toFixed(2)}, Available: ₹${availableBalance.toFixed(2)}`
        );
        setShowInsufficientBalance(true);
        return;
      }
    }

    // Validate GPU configuration
    if ((formData.gpu_count || 0) > 0 && !formData.gpu_type) {
      toast.error('Please select a GPU type when GPU count is greater than 0');
      return;
    }

    // Validate OS image
    const selectedOS = osImages.find((img) => img.name === formData.os_image);
    if (!selectedOS || !selectedOS.is_available) {
      toast.error('Please select an available OS image');
      return;
    }

    // Validate SSH configuration
    if (authMethod === 'password') {
      if (!formData.ssh_password || formData.ssh_password.length < 8) {
        toast.error('Password must be at least 8 characters long');
        return;
      }
      if (confirmPassword !== formData.ssh_password) {
        toast.error('Passwords do not match');
        return;
      }
    } else if (authMethod === 'ssh_key' && !formData.ssh_key_id) {
      toast.error('Please select an SSH key');
      return;
    }

    // Validate additional disks
    for (const disk of additionalDisks) {
      if (!disk.name || !disk.type) {
        toast.error('All disks must have a name and type');
        return;
      }
      if (disk.type !== 'existing_dv' && !disk.size_gb) {
        toast.error(`Disk ${disk.name} must have a size`);
        return;
      }
      if (disk.type === 'existing_dv' && !disk.existing_dv_name) {
        toast.error(`Disk ${disk.name} must have an existing data volume selected`);
        return;
      }
    }

    // Prepare VM template (name will be auto-generated by pool service)
    const selectedSSHKey = formData.ssh_key_id
      ? sshKeys.find((key) => key.id === formData.ssh_key_id)
      : null;

    const vmTemplate: any = {
      ...formData,
      name: `${poolName}-template`, // Temporary name, will be replaced by pool service
      ssh_public_key: selectedSSHKey?.public_key,
      ssh_password: formData.ssh_password || undefined,
      use_dynamic_ssh_injection: authMethod === 'ssh_key' && formData.ssh_key_id ? true : false,
      use_case: formData.use_case || 'development_testing',
      is_spot: formData.is_spot || false,
      user_data_script: userDataScript.trim() || undefined,
      cloud_init_template_id: selectedCloudInitTemplate?.id || undefined,
      additional_disks: additionalDisks.length > 0 ? additionalDisks : undefined,
    };

    // Remove undefined fields
    Object.keys(vmTemplate).forEach((key) => {
      if (vmTemplate[key] === undefined) {
        delete vmTemplate[key];
      }
    });

    const poolData: VMPoolCreate = {
      name: poolName.trim().toLowerCase(),
      description: poolDescription.trim() || undefined,
      replicas,
      vm_template: vmTemplate,
      project_id: formData.project_id,
    };

    createMutation.mutate(poolData);
  };

  const steps: Step[] = projectIdFromUrl
    ? ['pool', 'region', 'use_case', 'resources', 'os', 'ssh', 'advanced', 'disks', 'review']
    : ['project', 'pool', 'region', 'use_case', 'resources', 'os', 'ssh', 'advanced', 'disks', 'review'];

  const currentStepIndex = steps.indexOf(step);
  const totalSteps = steps.length;

  const canGoNext = () => {
    switch (step) {
      case 'project':
        return !!formData.project_id;
      case 'pool':
        return poolName.trim().length > 0 && replicas >= 1 && replicas <= 100;
      case 'region':
        return !!formData.region;
      case 'use_case':
        return !!formData.use_case;
      case 'resources':
        return true; // GPU is optional
      case 'os':
        return !!formData.os_image;
      case 'ssh':
        return authMethod === 'none' || (authMethod === 'ssh_key' && !!formData.ssh_key_id) || (authMethod === 'password' && !!formData.ssh_password && formData.ssh_password === confirmPassword);
      case 'advanced':
        return true; // Optional fields
      case 'disks':
        return !additionalDisks.some(disk => !disk.name || !disk.type || (disk.type !== 'existing_dv' && !disk.size_gb) || (disk.type === 'existing_dv' && !disk.existing_dv_name));
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canGoNext() && currentStepIndex < totalSteps - 1) {
      setStep(steps[currentStepIndex + 1]);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setStep(steps[currentStepIndex - 1]);
    }
  };

  // Error state for form validation
  const [errors, setErrors] = useState<{ poolName?: string }>({});

  // Helper function to validate pool name
  const validatePoolName = (name: string): string | undefined => {
    if (!name) {
      return 'Pool name is required';
    }
    if (name.length > 253) {
      return 'Name must be 253 characters or less';
    }
    const pattern = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/;
    if (!pattern.test(name)) {
      return 'Name must consist of lowercase letters (a-z), numbers (0-9), and hyphens (-). It must start and end with a letter or number.';
    }
    return undefined;
  };

  const getPeriodLabel = (period: VMBillingPeriod): string => {
    switch (period) {
      case 'minute': return 'min';
      case 'hour': return 'hr';
      case 'day': return 'day';
      case 'month': return 'mo';
      case 'quarter': return 'qtr';
      case 'annual': return 'yr';
      default: return period;
    }
  };

  // Calculate total cost (per VM * replicas)
  const totalCost = costEstimate ? costEstimate.period_cost * replicas : null;
  return (
    <div>
      <Header
        title="Create VM Pool"
        subtitle="Create a pool of identical virtual machines"
        actions={
          <button
            onClick={() => navigate('/vm-pools')}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to VM Pools
          </button>
        }
      />

      <div className="p-6 max-w-7xl mx-auto">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <button
                onClick={() => {
                  if (i <= currentStepIndex) {
                    setStep(s);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  step === s
                    ? 'bg-primary-100 text-primary-700'
                    : currentStepIndex > i
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                }`}
              >
                {currentStepIndex > i ? (
                  <CheckCircleIcon className="w-5 h-5" />
                ) : (
                  <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs">
                    {i + 1}
                  </span>
                )}
                {s === 'project' ? 'Project' :
                 s === 'pool' ? 'Pool Config' :
                 s === 'region' ? 'Region' :
                 s === 'use_case' ? 'Use Case' :
                 s === 'resources' ? 'Resources' :
                 s === 'os' ? 'OS Image' :
                 s === 'ssh' ? 'SSH Config' :
                 s === 'advanced' ? 'Advanced' :
                 s === 'disks' ? 'Disks' :
                 s === 'review' ? 'Review' :
                 'Unknown'}
              </button>
              {i < steps.length - 1 && <div className="w-12 h-px bg-gray-300"></div>}
            </React.Fragment>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">

            {/* Step 1: Project Selection */}
            {step === 'project' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Select Project
                </h2>
                <p className="text-gray-500 mb-6">
                  Choose the project where this VM pool will be created
                </p>
                
                {/* Searchable Project Dropdown */}
                <div className="space-y-4">
                  <div className="relative" ref={projectDropdownRef}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Project *
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setIsProjectDropdownOpen(false);
                          setShowCreateProjectModal(true);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Create new project"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>New</span>
                      </button>
                    </div>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                        className={`w-full px-4 py-3 text-left bg-white border-2 rounded-lg transition-colors flex items-center justify-between ${
                          formData.project_id
                            ? 'border-primary-500 bg-primary-50'
                            : isProjectDropdownOpen
                            ? 'border-primary-500'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {formData.project_id ? (
                            <>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {projects.find((p: any) => p.id === formData.project_id)?.name || 'Select a project'}
                                </div>
                                {projects.find((p: any) => p.id === formData.project_id)?.description && (
                                  <div className="text-sm text-gray-500 truncate">
                                    {projects.find((p: any) => p.id === formData.project_id)?.description}
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-500">Select a project...</span>
                          )}
                        </div>
                        <ChevronDownIcon
                          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                            isProjectDropdownOpen ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      {isProjectDropdownOpen && (
                        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-96 overflow-hidden">
                          {/* Search Input */}
                          <div className="p-3 border-b border-gray-200">
                            <div className="relative">
                              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="text"
                                value={projectSearchQuery}
                                onChange={(e) => setProjectSearchQuery(e.target.value)}
                                placeholder="Search projects..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                autoFocus
                              />
                              {projectSearchQuery && (
                                <button
                                  onClick={() => setProjectSearchQuery('')}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Project List */}
                          <div className="max-h-64 overflow-y-auto">
                            {projects
                              .filter((project: any) =>
                                project.name.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
                                project.description?.toLowerCase().includes(projectSearchQuery.toLowerCase())
                              )
                              .slice(0, 10)
                              .map((project: any) => (
                                <button
                                  key={project.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({ ...formData, project_id: project.id });
                                    setIsProjectDropdownOpen(false);
                                    setProjectSearchQuery('');
                                  }}
                                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                                    formData.project_id === project.id ? 'bg-primary-50' : ''
                                  }`}
                                >
                                  <div className="font-medium text-gray-900">{project.name}</div>
                                  {project.description && (
                                    <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                                      {project.description}
                                    </div>
                                  )}
                                </button>
                              ))}
                            {projects.filter((project: any) =>
                              project.name.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
                              project.description?.toLowerCase().includes(projectSearchQuery.toLowerCase())
                            ).length === 0 && (
                              <div className="px-4 py-8 text-center text-gray-500">
                                No projects found
                              </div>
                            )}
                          </div>
                          
                          {/* Create Project Button */}
                          <div className="p-3 border-t border-gray-200 bg-gray-50">
                            <button
                              type="button"
                              onClick={() => {
                                setIsProjectDropdownOpen(false);
                                setShowCreateProjectModal(true);
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                            >
                              <PlusIcon className="w-5 h-5" />
                              Create New Project
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Selected Project Info */}
                  {formData.project_id && (
                    <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-primary-900">
                            {projects.find((p: any) => p.id === formData.project_id)?.name}
                          </div>
                          {projects.find((p: any) => p.id === formData.project_id)?.description && (
                            <div className="text-sm text-primary-700 mt-1">
                              {projects.find((p: any) => p.id === formData.project_id)?.description}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, project_id: undefined })}
                          className="ml-4 text-primary-600 hover:text-primary-800"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Pool Configuration */}
            {step === 'pool' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Pool Configuration
                </h2>
                <p className="text-gray-500 mb-6">
                  Configure the VM pool name and size
                </p>
                <div className="space-y-6">
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pool Name *
                    </label>
                    <input
                      type="text"
                      value={poolName}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setPoolName(value);
                        const error = validatePoolName(value);
                        setErrors({ ...errors, poolName: error });
                      }}
                      className={`input w-full ${errors.poolName ? 'border-red-300' : ''}`}
                      placeholder="my-vm-pool"
                      required
                    />
                    {errors.poolName ? (
                      <p className="mt-1 text-xs text-red-600">{errors.poolName}</p>
                    ) : (
                      <p className="mt-1 text-sm text-gray-500">
                        Use lowercase letters, numbers, and hyphens only
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={poolDescription}
                      onChange={(e) => setPoolDescription(e.target.value)}
                      className="input w-full"
                      rows={3}
                      placeholder="Optional description for this VM pool"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of VMs (Replicas) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={replicasInputValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setReplicasInputValue(value);
                        const num = parseInt(value, 10);
                        if (!isNaN(num) && num >= 1 && num <= 100) {
                          setReplicas(num);
                        }
                      }}
                      onBlur={() => {
                        const num = parseInt(replicasInputValue, 10);
                        if (isNaN(num) || num < 1) {
                          setReplicasInputValue('1');
                          setReplicas(1);
                        } else if (num > 100) {
                          setReplicasInputValue('100');
                          setReplicas(100);
                        } else {
                          setReplicasInputValue(num.toString());
                          setReplicas(num);
                        }
                      }}
                      className="input w-full"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Number of identical VMs in the pool (1-100)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Region Selection */}
            {step === 'region' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Select Region
                </h2>
                <p className="text-gray-500 mb-6">
                  Choose the geographic region where your VM pool will be deployed
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {regionsForSelection.map((region) => (
                      <button
                        key={region.id}
                        onClick={() => {
                          if (region.isAvailable) {
                            setFormData({ ...formData, region: region.id });
                          }
                        }}
                        disabled={!region.isAvailable}
                        className={`relative p-6 border-2 rounded-xl text-left transition-all ${
                          formData.region === region.id
                            ? 'border-primary-500 bg-primary-50 shadow-md'
                            : region.isAvailable
                              ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer'
                              : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">{region.flag}</span>
                            <div>
                              <h3 className={`font-semibold text-lg ${
                                region.isAvailable ? 'text-gray-900' : 'text-gray-500'
                              }`}>
                                {region.name}
                              </h3>
                              <p className={`text-sm ${
                                region.isAvailable ? 'text-gray-600' : 'text-gray-400'
                              }`}>
                                {region.displayName}
                              </p>
                            </div>
                          </div>
                          {region.isAvailable && formData.region === region.id && (
                            <div className="flex-shrink-0">
                              <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                          {!region.isAvailable && (
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                                Coming Soon
                              </span>
                            </div>
                          )}
                        </div>
                        {region.description && (
                          <p className={`text-sm mt-2 ${
                            region.isAvailable ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {region.description}
                          </p>
                        )}
                        {region.isAvailable && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center gap-2 text-xs text-green-600">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium">Available Now</span>
                            </div>
                          </div>
                        )}
                      </button>
                    ))}
                </div>
                {formData.region && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm text-blue-800">
                          <strong>Selected:</strong> {regions.find(r => r.id === formData.region)?.displayName}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          Your VM pool will be deployed in this region. Region cannot be changed after creation.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Use Case Selection */}
            {step === 'use_case' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Select Use Case
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  Choose the appropriate use case for your VM pool. This determines network configuration and available options.
                </p>
                
                <div className="space-y-4">
                    {/* Development/Testing Option */}
                    <div
                      className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.use_case === 'development_testing'
                          ? 'border-primary-500 bg-primary-50 shadow-md'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                      }`}
                      onClick={() => {
                        setFormData({ 
                          ...formData, 
                          use_case: 'development_testing',
                          is_spot: formData.use_case === 'production' ? false : formData.is_spot
                        });
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            formData.use_case === 'development_testing'
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-gray-300'
                          }`}>
                            {formData.use_case === 'development_testing' && (
                              <div className="w-3 h-3 rounded-full bg-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg text-gray-900">
                                Development/Testing
                              </h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">
                              Ideal for development, testing, and non-production workloads. Uses bridge networking.
                            </p>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Bridge networking (no live migration)</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Spot VM option available (50% discount)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Spot VM Checkbox (only for Development/Testing) */}
                      {formData.use_case === 'development_testing' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.is_spot}
                              onChange={(e) => {
                                setFormData({ ...formData, is_spot: e.target.checked });
                              }}
                              className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  Use Spot VM
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  50% Discount
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">
                                Spot VMs use bridge networking and cannot be live migrated. Suitable for development and testing workloads.
                              </p>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Production Option */}
                    <div
                      className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.use_case === 'production'
                          ? 'border-primary-500 bg-primary-50 shadow-md'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                      }`}
                      onClick={() => {
                        setFormData({ 
                          ...formData, 
                          use_case: 'production',
                          is_spot: false
                        });
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            formData.use_case === 'production'
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-gray-300'
                          }`}>
                            {formData.use_case === 'production' && (
                              <div className="w-3 h-3 rounded-full bg-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg text-gray-900">
                                Production
                              </h3>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Recommended
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">
                              For production workloads requiring high availability and zero-downtime operations.
                            </p>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Masquerade networking (supports live migration)</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Zero-downtime maintenance and migrations</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                </div>
              </div>
            )}

            {/* Step 5: Resources */}
            {step === 'resources' && (
              <div className="space-y-6">
                {/* Quick Templates */}
                <div className="card">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Quick Templates
                  </h2>
                    <p className="text-sm text-gray-500 mb-4">
                      Select a template to quickly configure CPU, Memory and Storage.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {quickTemplates.map((template) => (
                        <button
                          key={template.name}
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              cpu_cores: template.cpu,
                              memory_gb: template.memory,
                              storage_gb: template.storage,
                            });
                            setCpuInputValue(template.cpu.toString());
                            setMemoryInputValue(template.memory.toString());
                            setStorageInputValue(template.storage.toString());
                          }}
                          className={`p-4 border-2 rounded-lg transition-colors text-left ${
                            'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1">{template.name}</div>
                          <div className="text-xs text-gray-600 space-y-0.5">
                            <div>{template.cpu} vCPU</div>
                            <div>{template.memory} GB RAM</div>
                            <div>{template.storage} GB Storage</div>
                          </div>
                          <div className="text-xs text-gray-500 mt-2">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                {/* Compute Classes */}
                <div className="card">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Compute Classes
                  </h2>
                    <p className="text-sm text-gray-500 mb-4">
                      Choose a class below Quick Templates for better workload fit.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {computeClasses.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => {
                            setSelectedComputeClass(template.id);
                            setSelectedClassConfiguration('');
                            setFormData({
                              ...formData,
                              cpu_cores: template.cpu,
                              memory_gb: template.memory,
                              storage_gb: template.storage,
                            });
                            setCpuInputValue(template.cpu.toString());
                            setMemoryInputValue(template.memory.toString());
                            setStorageInputValue(template.storage.toString());
                          }}
                          className={`p-4 border-2 rounded-lg transition-colors text-left ${
                            selectedComputeClass === template.id
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1">{template.name}</div>
                          <div className="text-xs text-gray-500 mt-2">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                <div className="card">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Compute Resources
                  </h2>
                    <p className="text-xs text-gray-500 mb-3">
                      CPU and Memory are locked to class configuration. Toggle `ENABLE_MANUAL_RESOURCE_OVERRIDE` to re-enable manual edits.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          VM Configuration (Class Presets)
                        </label>
                        <select
                          value={selectedClassConfiguration}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedClassConfiguration(value);
                            const options: ReadonlyArray<ClassConfigOption> =
                              classConfigurations[selectedComputeClass as keyof typeof classConfigurations] || [];
                            const selected = options.find((opt: ClassConfigOption) => opt.label === value);
                            if (selected) {
                              setFormData({ ...formData, cpu_cores: selected.cpu, memory_gb: selected.memory });
                              setCpuInputValue(selected.cpu.toString());
                              setMemoryInputValue(selected.memory.toString());
                            }
                          }}
                          className="input w-full"
                        >
                          <option value="">Select a pre-defined VM configuration</option>
                          {(classConfigurations[selectedComputeClass as keyof typeof classConfigurations] || []).map((opt) => (
                            <option key={opt.label} value={opt.label}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CPU Cores *
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="32"
                          value={cpuInputValue}
                          onChange={(e) => {
                            if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                            const value = e.target.value;
                            setCpuInputValue(value);
                            const numValue = parseInt(value);
                            if (!isNaN(numValue) && numValue >= 1) {
                              setSelectedComputeClass('custom');
                              setFormData({ ...formData, cpu_cores: numValue });
                            }
                          }}
                          onBlur={(e) => {
                            if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                            const numValue = parseInt(e.target.value);
                            if (isNaN(numValue) || numValue < 1) {
                              setCpuInputValue('1');
                              setFormData({ ...formData, cpu_cores: 1 });
                            }
                          }}
                          disabled={!ENABLE_MANUAL_RESOURCE_OVERRIDE}
                          className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-60 disabled:cursor-not-allowed ${
                            formData.cpu_cores < 1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                          }`}
                          required
                        />
                        {formData.cpu_cores < 1 && (
                          <p className="mt-1 text-xs text-red-600 font-medium">
                            Minimum: 1 CPU core required
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Memory (GB) *
                        </label>
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={memoryInputValue}
                          onChange={(e) => {
                            if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                            const value = e.target.value;
                            setMemoryInputValue(value);
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue) && numValue >= 0.5) {
                              setSelectedComputeClass('custom');
                              setFormData({ ...formData, memory_gb: numValue });
                            }
                          }}
                          onBlur={(e) => {
                            if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                            const numValue = parseFloat(e.target.value);
                            if (isNaN(numValue) || numValue < 0.5) {
                              setMemoryInputValue('0.5');
                              setFormData({ ...formData, memory_gb: 0.5 });
                            }
                          }}
                          disabled={!ENABLE_MANUAL_RESOURCE_OVERRIDE}
                          className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-60 disabled:cursor-not-allowed ${
                            formData.memory_gb < 0.5 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                          }`}
                          required
                        />
                        {formData.memory_gb < 0.5 && (
                          <p className="mt-1 text-xs text-red-600 font-medium">
                            Minimum: 0.5 GB required
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Storage (GB) *
                        </label>
                        <input
                          type="number"
                          min="10"
                          value={storageInputValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            setStorageInputValue(value);
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue) && numValue >= 10) {
                              setFormData({ ...formData, storage_gb: numValue });
                            }
                          }}
                          onBlur={(e) => {
                            const numValue = parseFloat(e.target.value);
                            if (isNaN(numValue) || numValue < 10) {
                              setStorageInputValue('10');
                              setFormData({ ...formData, storage_gb: 10 });
                            }
                          }}
                          className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                            formData.storage_gb < 10 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                          }`}
                          required
                        />
                        {formData.storage_gb < 10 && (
                          <p className="mt-1 text-xs text-red-600 font-medium">
                            Minimum: 10 GB required
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                {VM_SHOW_GPU_CONFIGURATION && (
                <div className="card">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    GPU Configuration (Optional)
                  </h2>
                    <p className="text-gray-500 mb-6">
                      Only GPUs with available capacity are listed. Choose one or stay on CPU only.
                    </p>

                  {loadingGPU ? (
                    <div className="flex justify-center py-12">
                      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Search GPUs (e.g., A100, RTX, H100)..."
                              value={gpuSearchQuery}
                              onChange={(e) => {
                                setGpuSearchQuery(e.target.value);
                                setGpuCurrentPage(1);
                              }}
                              className="input w-full"
                            />
                          </div>
                        </div>
                      </div>

                      {/* No GPU Option */}
                      <button
                        onClick={() => handleSelectGPU(null)}
                        className={`w-full p-4 border-2 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-colors text-left ${
                          !selectedGPU ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
                        }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-gray-100 rounded-xl">
                              <ServerStackIcon className="w-8 h-8 text-gray-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                CPU Only Instance
                              </h3>
                              <p className="text-sm text-gray-500">
                                No GPU - ideal for general purpose workloads
                              </p>
                            </div>
                          </div>
                        </button>

                      {/* GPU Options */}
                      {filteredGpus.length === 0 ? (
                        <div className="text-center py-12">
                          <CpuChipIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500">No GPUs found matching your criteria</p>
                        </div>
                      ) : (
                        <>
                          {paginatedGpus.map((gpu: GPUPricing) => {
                            const availability = getGPUAvailability(gpu.gpu_type);
                            const availableCount = parseFloat((availability?.available_gpus ?? 0).toFixed(2));
                            const totalCount = availability?.total_gpus ?? 0;
                            const isAvailable = availability !== undefined && availableCount > 0;
                            const isSelected = selectedGPU?.gpu_type === gpu.gpu_type;

                            return (
                              <div
                                key={gpu.gpu_type}
                                className={`w-full p-4 border-2 rounded-xl text-left ${
                                  isSelected
                                    ? 'border-primary-500 bg-primary-50'
                                    : isAvailable
                                      ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer'
                                      : 'border-gray-200 bg-gray-50'
                                } transition-colors`}
                                onClick={() => isAvailable && handleSelectGPU(gpu)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-xl ${
                                      isAvailable ? 'bg-green-100' : 'bg-red-100'
                                    }`}>
                                      <CpuChipIcon className={`w-8 h-8 ${
                                        isAvailable ? 'text-green-600' : 'text-red-500'
                                      }`} />
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <h3 className={`font-semibold ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
                                          {gpu.gpu_type}
                                        </h3>
                                        {isAvailable ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                            {availableCount}/{totalCount} Available
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                            0/{totalCount} Available
                                          </span>
                                        )}
                                      </div>
                                      <p className={`text-sm ${isAvailable ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {gpu.memory_gb}GB VRAM
                                        {gpu.cuda_cores > 0 && ` • ${gpu.cuda_cores.toLocaleString()} CUDA Cores`}
                                        {gpu.tensor_cores > 0 && ` • ${gpu.tensor_cores} Tensor Cores`}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className={`text-lg font-semibold ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
                                      ₹{gpu.price_per_minute.toFixed(2)}/min
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      ₹{(gpu.price_per_minute * 60).toFixed(0)}/hr
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Pagination */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-4">
                              <div className="text-sm text-gray-600">
                                Showing {startIndex + 1}-{Math.min(endIndex, filteredGpus.length)} of {filteredGpus.length} GPUs
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setGpuCurrentPage(Math.max(1, gpuCurrentPage - 1))}
                                  disabled={gpuCurrentPage === 1}
                                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                    gpuCurrentPage === 1
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  Previous
                                </button>
                                <div className="flex gap-1">
                                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                      key={page}
                                      onClick={() => setGpuCurrentPage(page)}
                                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                        page === gpuCurrentPage
                                          ? 'bg-primary-600 text-white'
                                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      {page}
                                    </button>
                                  ))}
                                </div>
                                <button
                                  onClick={() => setGpuCurrentPage(Math.min(totalPages, gpuCurrentPage + 1))}
                                  disabled={gpuCurrentPage === totalPages}
                                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                    gpuCurrentPage === totalPages
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* GPU Count Input - shown when GPU is selected */}
                  {selectedGPU && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GPU Count *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        value={gpuCountInputValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          setGpuCountInputValue(value);
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 1 && numValue <= 8) {
                            setFormData({
                              ...formData,
                              gpu_count: numValue,
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const numValue = parseInt(e.target.value);
                          if (isNaN(numValue) || numValue < 1) {
                            setGpuCountInputValue('1');
                            setFormData({
                              ...formData,
                              gpu_count: 1,
                            });
                          } else if (numValue > 8) {
                            setGpuCountInputValue('8');
                            setFormData({
                              ...formData,
                              gpu_count: 8,
                            });
                          }
                        }}
                        className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                          (formData.gpu_count || 0) < 1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                        required
                      />
                      {(formData.gpu_count || 0) >= 1 && (
                        <p className="mt-1 text-xs text-gray-500">
                          Number of {selectedGPU.gpu_type} GPUs (1-8)
                        </p>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>
            )}

            {/* Step 6: OS Image Selection */}
            {step === 'os' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Operating System Image
                </h2>
                <p className="text-gray-500 mb-6">
                  Choose an OS image for your VM pool
                </p>
                {loadingOSImages ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(osImagesByCategory).map(([category, images]) => (
                      <div key={category}>
                        <h3 className="text-sm font-medium text-gray-700 mb-2 capitalize">
                          {category === 'general' ? 'General Purpose' :
                           category === 'ml' ? 'Machine Learning / AI' :
                           category === 'automation' ? 'Automation' :
                           'Custom'}
                        </h3>
                        <div className="space-y-2">
                          {images.map((img) => (
                            <button
                              key={img.id}
                              onClick={() => setFormData({ ...formData, os_image: img.name })}
                              disabled={!img.is_available}
                              className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                                formData.os_image === img.name
                                  ? 'border-primary-500 bg-primary-50'
                                  : img.is_available
                                    ? 'border-gray-200 hover:border-gray-300'
                                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{img.display_name}</h4>
                                  {img.description && (
                                    <p className="text-sm text-gray-500 mt-1">{img.description}</p>
                                  )}
                                </div>
                                {!img.is_available && (
                                  <span className="text-xs text-gray-400">Coming Soon</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 7: SSH Configuration */}
            {step === 'ssh' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  SSH Configuration
                </h2>
                <p className="text-gray-500 mb-6">
                  Choose how to authenticate to your VMs (optional)
                </p>
                <div className="space-y-6">
                  {/* Authentication Method Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Authentication Method
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                        <input
                          type="radio"
                          name="authMethod"
                          value="none"
                          checked={authMethod === 'none'}
                          onChange={(e) => {
                            setAuthMethod('none');
                            setFormData({ ...formData, ssh_key_id: undefined, ssh_password: undefined });
                          }}
                          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">No Authentication (Set Later)</div>
                          <p className="text-sm text-gray-500 mt-1">
                            You can set an SSH key or password after VM pool creation
                          </p>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                        <input
                          type="radio"
                          name="authMethod"
                          value="ssh_key"
                          checked={authMethod === 'ssh_key'}
                          onChange={(e) => {
                            setAuthMethod('ssh_key');
                            setFormData({ ...formData, ssh_password: undefined });
                            if (sshKeys.length > 0 && !formData.ssh_key_id) {
                              const defaultKey = sshKeys.find((key) => key.is_default);
                              if (defaultKey) {
                                setFormData({ ...formData, ssh_key_id: defaultKey.id, ssh_password: undefined });
                              }
                            }
                          }}
                          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">SSH Key</div>
                          <p className="text-sm text-gray-500 mt-1">
                            Use an SSH public key for secure, passwordless authentication
                          </p>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                        <input
                          type="radio"
                          name="authMethod"
                          value="password"
                          checked={authMethod === 'password'}
                          onChange={(e) => {
                            setAuthMethod('password');
                            setFormData({ ...formData, ssh_key_id: undefined });
                          }}
                          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Password</div>
                          <p className="text-sm text-gray-500 mt-1">
                            Set a password for SSH access (less secure than SSH key)
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* SSH Key Selection */}
                  {authMethod === 'ssh_key' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Select SSH Key *
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setIsSshKeyDropdownOpen(false);
                            setShowCreateSSHKeyModal(true);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Create new SSH key"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>New</span>
                        </button>
                      </div>
                      {sshKeys.length === 0 ? (
                        <div className="space-y-3">
                          <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
                            <p className="text-sm text-gray-500 mb-3">No SSH keys found</p>
                            <button
                              type="button"
                              onClick={() => setShowCreateSSHKeyModal(true)}
                              className="btn-primary text-sm"
                            >
                              Create SSH Key
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="relative" ref={sshKeyDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsSshKeyDropdownOpen(!isSshKeyDropdownOpen)}
                            className={`w-full px-4 py-3 text-left bg-white border-2 rounded-lg transition-colors flex items-center justify-between ${
                              formData.ssh_key_id
                                ? 'border-primary-500 bg-primary-50'
                                : isSshKeyDropdownOpen
                                ? 'border-primary-500'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {formData.ssh_key_id ? (
                                <>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 truncate">
                                      {sshKeys.find((key) => key.id === formData.ssh_key_id)?.name || 'Select an SSH key'}
                                    </div>
                                    {sshKeys.find((key) => key.id === formData.ssh_key_id)?.is_default && (
                                      <div className="text-sm text-gray-500 truncate">
                                        Default key
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <span className="text-gray-500">Select an SSH key...</span>
                              )}
                            </div>
                            <ChevronDownIcon
                              className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                                isSshKeyDropdownOpen ? 'transform rotate-180' : ''
                              }`}
                            />
                          </button>
                          
                          {isSshKeyDropdownOpen && (
                            <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-96 overflow-hidden">
                              {/* Search Input */}
                              <div className="p-3 border-b border-gray-200">
                                <div className="relative">
                                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                  <input
                                    type="text"
                                    value={sshKeySearchQuery}
                                    onChange={(e) => setSshKeySearchQuery(e.target.value)}
                                    placeholder="Search SSH keys..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    autoFocus
                                  />
                                  {sshKeySearchQuery && (
                                    <button
                                      type="button"
                                      onClick={() => setSshKeySearchQuery('')}
                                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                      <XMarkIcon className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              
                              {/* SSH Key List */}
                              <div className="max-h-64 overflow-y-auto">
                                {sshKeys
                                  .filter((key) =>
                                    key.name.toLowerCase().includes(sshKeySearchQuery.toLowerCase())
                                  )
                                  .slice(0, 10)
                                  .map((key) => (
                                    <button
                                      key={key.id}
                                      type="button"
                                      onClick={() => {
                                        setFormData({ ...formData, ssh_key_id: key.id });
                                        setIsSshKeyDropdownOpen(false);
                                        setSshKeySearchQuery('');
                                      }}
                                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                                        formData.ssh_key_id === key.id ? 'bg-primary-50' : ''
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-gray-900 truncate">
                                            {key.name}
                                          </div>
                                          {key.is_default && (
                                            <div className="text-sm text-gray-500 mt-1">
                                              Default key
                                            </div>
                                          )}
                                        </div>
                                        {key.is_default && (
                                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                            Default
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  ))}
                                {sshKeys.filter((key) =>
                                  key.name.toLowerCase().includes(sshKeySearchQuery.toLowerCase())
                                ).length === 0 && (
                                  <div className="px-4 py-8 text-center text-gray-500">
                                    No SSH keys found
                                  </div>
                                )}
                              </div>
                              
                              {/* Create SSH Key Button */}
                              <div className="p-3 border-t border-gray-200 bg-gray-50">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsSshKeyDropdownOpen(false);
                                    setShowCreateSSHKeyModal(true);
                                  }}
                                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                                >
                                  <PlusIcon className="w-5 h-5" />
                                  Create New SSH Key
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {formData.ssh_key_id && (
                        <div className="mt-2 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-primary-900">
                                {sshKeys.find((key) => key.id === formData.ssh_key_id)?.name}
                              </div>
                              <div className="text-sm text-primary-700 mt-1">
                                ✓ SSH key selected. You'll be able to connect using this key.
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, ssh_key_id: undefined })}
                              className="ml-4 text-primary-600 hover:text-primary-800"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Password Input */}
                  {authMethod === 'password' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SSH Password *
                        </label>
                        <input
                          type="password"
                          value={formData.ssh_password || ''}
                          onChange={(e) => setFormData({ ...formData, ssh_password: e.target.value })}
                          className="input w-full"
                          placeholder="Enter password for SSH access"
                          required
                          minLength={8}
                        />
                        <p className="mt-2 text-sm text-gray-500">
                          Minimum 8 characters. This password will be set for the default user (ubuntu).
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm Password *
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={`input w-full ${
                            confirmPassword && formData.ssh_password && confirmPassword !== formData.ssh_password
                              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                              : ''
                          }`}
                          placeholder="Confirm password"
                          required
                          minLength={8}
                        />
                        {confirmPassword && formData.ssh_password && confirmPassword !== formData.ssh_password && (
                          <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                        )}
                        {confirmPassword && formData.ssh_password && confirmPassword === formData.ssh_password && (
                          <p className="mt-1 text-sm text-green-600">✓ Passwords match</p>
                        )}
                      </div>
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          <strong>Security Note:</strong> SSH key authentication is more secure than password authentication. Consider using an SSH key for production workloads.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 7: Advanced Configuration */}
            {step === 'advanced' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Advanced Configuration
                </h2>
                <p className="text-gray-500 mb-6">
                  Configure cloud-init templates and user data scripts (optional)
                </p>
                <div className="space-y-6">
                  {/* Cloud-init Template Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cloud-init Template (Optional)
                    </label>
                    <p className="text-sm text-gray-500 mb-3">
                      Select a pre-configured cloud-init template or leave empty for auto-selection
                    </p>
                    {cloudInitTemplates.length === 0 ? (
                      <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
                        <p className="text-sm text-gray-500 mb-3">No templates available</p>
                        <button
                          type="button"
                          onClick={() => navigate('/cloudinit-templates')}
                          className="btn-primary text-sm"
                        >
                          Create Template
                        </button>
                      </div>
                    ) : (
                      <select
                        value={selectedCloudInitTemplate?.id || ''}
                        onChange={(e) => {
                          const template = cloudInitTemplates.find(t => t.id === e.target.value);
                          setSelectedCloudInitTemplate(template || null);
                        }}
                        className="input w-full"
                      >
                        <option value="">Auto-select (based on OS)</option>
                        {cloudInitTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name} {template.is_public ? '(Public)' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {selectedCloudInitTemplate && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm">
                          <div className="font-medium text-blue-900">{selectedCloudInitTemplate.name}</div>
                          {selectedCloudInitTemplate.description && (
                            <div className="text-blue-700 mt-1">{selectedCloudInitTemplate.description}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* User Data Script */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      User Data Script (Optional)
                    </label>
                    <p className="text-sm text-gray-500 mb-3">
                      Add custom commands to run during VM initialization (one command per line). These will be appended to the cloud-init runcmd section.
                    </p>
                    <textarea
                      value={userDataScript}
                      onChange={(e) => setUserDataScript(e.target.value)}
                      rows={8}
                      className="input w-full font-mono text-sm"
                      placeholder="# Example commands:&#10;apt-get update&#10;apt-get install -y nginx&#10;systemctl enable nginx"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Each line will be executed as a separate command during VM boot.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 8: Additional Disks */}
            {step === 'disks' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Additional Disks
                </h2>
                <p className="text-gray-500 mb-6">
                  Add additional storage disks to your VMs (optional)
                </p>
                <div className="space-y-4">
                  {additionalDisks.map((disk, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-gray-900">Disk {index + 1}</h3>
                        <button
                          type="button"
                          onClick={() => {
                            setAdditionalDisks(additionalDisks.filter((_, i) => i !== index));
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Disk Type *
                          </label>
                          <select
                            value={disk.type}
                            onChange={(e) => {
                              const newDisks = [...additionalDisks];
                              newDisks[index] = {
                                ...newDisks[index],
                                type: e.target.value as 'image' | 'blank' | 'existing_dv',
                                size_gb: e.target.value === 'existing_dv' ? undefined : newDisks[index].size_gb,
                                existing_dv_name: e.target.value === 'existing_dv' ? newDisks[index].existing_dv_name : undefined,
                              };
                              setAdditionalDisks(newDisks);
                            }}
                            className="input w-full"
                          >
                            <option value="blank">Blank Disk</option>
                            <option value="image">From Image</option>
                            <option value="existing_dv">Existing Data Volume</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Disk Name *
                          </label>
                          <input
                            type="text"
                            value={disk.name}
                            onChange={(e) => {
                              const newDisks = [...additionalDisks];
                              newDisks[index] = { ...newDisks[index], name: e.target.value };
                              setAdditionalDisks(newDisks);
                            }}
                            className="input w-full"
                            placeholder="e.g., disk1"
                            required
                          />
                        </div>
                        {disk.type !== 'existing_dv' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Size (GB) *
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={disk.size_gb || ''}
                              onChange={(e) => {
                                const newDisks = [...additionalDisks];
                                newDisks[index] = { ...newDisks[index], size_gb: parseFloat(e.target.value) || undefined };
                                setAdditionalDisks(newDisks);
                              }}
                              className="input w-full"
                              required
                            />
                          </div>
                        )}
                        {disk.type === 'existing_dv' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Data Volume *
                            </label>
                            <select
                              value={disk.existing_dv_name || ''}
                              onChange={(e) => {
                                const selectedVolume = dataVolumes.find((v: any) => v.k8s_datavolume_name === e.target.value);
                                const newDisks = [...additionalDisks];
                                newDisks[index] = {
                                  ...newDisks[index],
                                  existing_dv_name: e.target.value,
                                  existing_dv_namespace: selectedVolume?.namespace || formData.project_id ? `proj-${formData.project_id}` : undefined,
                                };
                                setAdditionalDisks(newDisks);
                              }}
                              className="input w-full"
                              required
                            >
                              <option value="">Select a data volume...</option>
                              {dataVolumes
                                .filter((v: any) => v.status === 'ready' || v.status === 'Succeeded')
                                .map((volume: any) => (
                                  <option key={volume.id} value={volume.k8s_datavolume_name || volume.name}>
                                    {volume.name} ({volume.size_gb} GB)
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}
                        {disk.type === 'image' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Source Image
                            </label>
                            <select
                              value={disk.source_image_id || ''}
                              onChange={(e) => {
                                const newDisks = [...additionalDisks];
                                newDisks[index] = { ...newDisks[index], source_image_id: e.target.value || undefined };
                                setAdditionalDisks(newDisks);
                              }}
                              className="input w-full"
                            >
                              <option value="">Select an image...</option>
                              {osImages.map((img) => (
                                <option key={img.id} value={img.id}>
                                  {img.display_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bus Type
                          </label>
                          <select
                            value={disk.bus_type || 'virtio'}
                            onChange={(e) => {
                              const newDisks = [...additionalDisks];
                              newDisks[index] = { ...newDisks[index], bus_type: e.target.value as any };
                              setAdditionalDisks(newDisks);
                            }}
                            className="input w-full"
                          >
                            <option value="virtio">VirtIO</option>
                            <option value="sata">SATA</option>
                            <option value="scsi">SCSI</option>
                            <option value="usb">USB</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setAdditionalDisks([
                        ...additionalDisks,
                        {
                          name: `disk${additionalDisks.length + 1}`,
                          type: 'blank',
                          size_gb: 10,
                          bus_type: 'virtio',
                        },
                      ]);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add Disk
                  </button>
                </div>
              </div>
            )}

            {/* Step 9: Review */}
            {step === 'review' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Review Configuration
                </h2>
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Pool Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-blue-700">Pool Name</p>
                          <p className="font-medium text-blue-900">{poolName}</p>
                        </div>
                        <div>
                          <p className="text-blue-700">Replicas</p>
                          <p className="font-medium text-blue-900">{replicas} VMs</p>
                        </div>
                        {poolDescription && (
                          <div className="col-span-2">
                            <p className="text-blue-700">Description</p>
                            <p className="font-medium text-blue-900">{poolDescription}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Project</p>
                        <p className="font-medium">
                          {projects.find((p: any) => p.id === formData.project_id)?.name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Region</p>
                        <p className="font-medium flex items-center gap-2">
                          <span>{regions.find(r => r.id === formData.region)?.flag}</span>
                          <span>{regions.find(r => r.id === formData.region)?.displayName || 'N/A'}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">CPU Cores</p>
                        <p className="font-medium">{formData.cpu_cores}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Memory</p>
                        <p className="font-medium">{formData.memory_gb} GB</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Storage</p>
                        <p className="font-medium">{formData.storage_gb} GB</p>
                      </div>
                      {VM_SHOW_GPU_CONFIGURATION && (
                      <div>
                        <p className="text-sm text-gray-500">GPU</p>
                        <p className="font-medium">
                          {(formData.gpu_count || 0) > 0
                            ? `${formData.gpu_count}x ${formData.gpu_type}`
                            : 'None'}
                        </p>
                      </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500">OS Image</p>
                        <p className="font-medium">
                          {osImages.find((img) => img.name === formData.os_image)?.display_name || formData.os_image}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Billing Period</p>
                        <p className="font-medium capitalize">{formData.billing_period}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Use Case</p>
                        <p className="font-medium capitalize">
                          {formData.use_case === 'production' ? 'Production' : 'Development/Testing'}
                          {formData.is_spot && formData.use_case === 'development_testing' && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Spot VM (50% off)
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Network Type</p>
                        <p className="font-medium">
                          {formData.use_case === 'production' ? 'Masquerade (Live Migration)' : 'Bridge'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">SSH Authentication</p>
                        <p className="font-medium">
                          {authMethod === 'ssh_key' ? 'SSH Key' : authMethod === 'password' ? 'Password' : 'None (Set Later)'}
                        </p>
                      </div>
                      {selectedCloudInitTemplate && (
                        <div>
                          <p className="text-sm text-gray-500">Cloud-init Template</p>
                          <p className="font-medium">{selectedCloudInitTemplate.name}</p>
                        </div>
                      )}
                      {userDataScript && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500">User Data Script</p>
                          <p className="font-medium text-xs font-mono bg-gray-100 p-2 rounded">
                            {userDataScript.split('\n').slice(0, 3).join('\n')}
                            {userDataScript.split('\n').length > 3 && '...'}
                          </p>
                        </div>
                      )}
                      {additionalDisks.length > 0 && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500 mb-2">Additional Disks</p>
                          <div className="space-y-2">
                            {additionalDisks.map((disk, idx) => (
                              <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                                <span className="font-medium">{disk.name}</span> - {disk.type}
                                {disk.size_gb && ` (${disk.size_gb} GB)`}
                                {disk.existing_dv_name && ` - ${disk.existing_dv_name}`}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {costEstimate && (
                      <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-2">Cost Summary</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Per VM ({formData.billing_period}):</span>
                            <span className="font-medium">₹{costEstimate.period_cost.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold text-blue-600">
                            <span>Total Pool ({replicas} VMs):</span>
                            <span>₹{totalCost?.toFixed(2) || '0.00'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6">
              <button
                onClick={handleBack}
                disabled={currentStepIndex === 0}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {currentStepIndex < totalSteps - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (step === 'project' && !formData.project_id) ||
                    (step === 'pool' && (!poolName.trim() || replicas < 1 || replicas > 100 || !!errors.poolName)) ||
                    (step === 'region' && !formData.region) ||
                    (step === 'use_case' && !formData.use_case) ||
                    (step === 'resources' && (formData.cpu_cores < 1 || formData.memory_gb < 0.5 || formData.storage_gb < 10 || ((formData.gpu_count || 0) > 0 && !formData.gpu_type))) ||
                    (step === 'os' && !formData.os_image) ||
                    (step === 'ssh' && authMethod === 'ssh_key' && !formData.ssh_key_id) ||
                    (step === 'ssh' && authMethod === 'password' && (!formData.ssh_password || formData.ssh_password.length < 8 || confirmPassword !== formData.ssh_password))
                  }
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={
                    !poolName.trim() || 
                    !formData.project_id || 
                    createMutation.isPending ||
                    !!errors.poolName ||
                    ((formData.gpu_count || 0) > 0 && !formData.gpu_type)
                  }
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Pool'}
                </button>
              )}
            </div>
          </div>

          {/* Pricing Sidebar */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="card lg:sticky lg:top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Summary</h3>

              <div className="space-y-3 text-sm mb-4">
                {formData.region && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Region</span>
                    <span className="font-medium flex items-center gap-1.5">
                      <span>{regions.find(r => r.id === formData.region)?.flag}</span>
                      <span>{regions.find(r => r.id === formData.region)?.name}</span>
                    </span>
                  </div>
                )}
                {poolName && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pool Name</span>
                    <span className="font-medium">{poolName}</span>
                  </div>
                )}
                {replicas > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Replicas</span>
                    <span className="font-medium">{replicas} VMs</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">CPU</span>
                  <span className="font-medium">{formData.cpu_cores} vCPU</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Memory</span>
                  <span className="font-medium">{formData.memory_gb} GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Storage</span>
                  <span className="font-medium">{formData.storage_gb} GB</span>
                </div>
                {(formData.gpu_count || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">GPU</span>
                    <span className="font-medium">
                      {formData.gpu_count}x {formData.gpu_type}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Billing</span>
                  <span className="font-medium capitalize">
                    {formData.billing_period}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Use Case</span>
                  <span className="font-medium capitalize">
                    {formData.use_case === 'production' ? 'Production' : 'Development/Testing'}
                    {formData.is_spot && formData.use_case === 'development_testing' && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Spot
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Network</span>
                  <span className="font-medium text-xs">
                    {formData.use_case === 'production' ? 'Masquerade' : 'Bridge'}
                  </span>
                </div>
              </div>

              <hr className="my-4" />

              {/* Billing Period Selector */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Billing Period</h4>
                <div className="grid grid-cols-2 gap-2">
                  {billingPeriods.map((period) => (
                    <button
                      key={period.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, billing_period: period.value })}
                      className={`p-2 border-2 rounded-lg text-center text-xs font-medium transition-colors ${
                        formData.billing_period === period.value
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              </div>

              <hr className="my-4" />

              {/* Cost Breakdown */}
              {isEstimatingCost ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                </div>
              ) : costEstimate ? (
                <div className="space-y-2 text-xs">
                  {costEstimate.hourly_cpu_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-blue-50">
                      <span className="text-gray-700 font-medium">CPU</span>
                      <span className="text-blue-700 font-semibold">₹{costEstimate.hourly_cpu_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_memory_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-green-50">
                      <span className="text-gray-700 font-medium">Memory</span>
                      <span className="text-green-700 font-semibold">₹{costEstimate.hourly_memory_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_storage_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-purple-50">
                      <span className="text-gray-700 font-medium">Storage</span>
                      <span className="text-purple-700 font-semibold">₹{costEstimate.hourly_storage_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_gpu_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-orange-50">
                      <span className="text-gray-700 font-medium">GPU</span>
                      <span className="text-orange-700 font-semibold">₹{costEstimate.hourly_gpu_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_gpu_memory_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-teal-50">
                      <span className="text-gray-700 font-medium">GPU Memory</span>
                      <span className="text-teal-700 font-semibold">₹{costEstimate.hourly_gpu_memory_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.discount_percent > 0 && (
                    <div className="flex justify-between items-center py-1.5 px-2 rounded bg-green-100 mt-2">
                      <span className="text-green-800 font-medium">Period Discount</span>
                      <span className="text-green-800 font-bold">{costEstimate.discount_percent.toFixed(1)}%</span>
                    </div>
                  )}
                  {costEstimate.is_spot && costEstimate.spot_discount_applied && (
                    <div className="flex justify-between items-center py-1.5 px-2 rounded bg-green-200 mt-2">
                      <span className="text-green-900 font-medium">Spot Discount</span>
                      <span className="text-green-900 font-bold">50%</span>
                    </div>
                  )}
                  {costEstimate.is_spot && costEstimate.spot_discount_applied && (
                    <div className="py-1.5 px-2 rounded bg-gray-100 mt-2">
                      <div className="flex justify-between items-center text-xs text-gray-600 mb-1">
                        <span>Regular Price</span>
                        <span className="line-through">₹{(costEstimate.period_cost * 2).toFixed(2)}/{getPeriodLabel(costEstimate.billing_period)}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-green-700 font-medium">
                        <span>You Save</span>
                        <span>₹{(costEstimate.period_cost).toFixed(2)}/{getPeriodLabel(costEstimate.billing_period)}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-lg pt-3 border-t-2 border-gray-200 mt-2">
                    <span className="font-semibold text-gray-900">Per VM</span>
                    <span className={`font-bold ${costEstimate.is_spot ? 'text-green-600' : 'text-primary-600'}`}>
                      ₹{costEstimate.period_cost.toFixed(2)}/{getPeriodLabel(costEstimate.billing_period)}
                      {costEstimate.is_spot && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Spot
                        </span>
                      )}
                    </span>
                  </div>
                  {replicas > 1 && (
                    <div className="flex justify-between items-center text-xl pt-2 border-t border-gray-200 mt-2">
                      <span className="font-semibold text-gray-900">Total Pool</span>
                      <span className={`font-bold ${costEstimate.is_spot ? 'text-green-600' : 'text-primary-600'}`}>
                        ₹{totalCost?.toFixed(2) || '0.00'}/{getPeriodLabel(costEstimate.billing_period)}
                      </span>
                    </div>
                  )}
                  <div className="text-xs text-gray-500 text-right pt-1">
                    ≈ ₹{costEstimate.hourly_total_cost.toFixed(2)}/hr {replicas > 1 && `(per VM)`}
                  </div>
                  {replicas > 1 && (
                    <div className="text-xs text-gray-500 text-right">
                      ≈ ₹{(costEstimate.hourly_total_cost * replicas).toFixed(2)}/hr (total)
                    </div>
                  )}
                  {costEstimate.billing_period !== 'minute' && (
                    <div className="text-xs text-gray-400 text-center pt-2 border-t border-gray-100 mt-2">
                      ≈ ₹{(costEstimate.period_cost / costEstimate.period_hours / 60).toFixed(4)}/min
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-400 text-center py-4">
                  Configure resources to see cost estimate
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Insufficient Balance Modal */}
      {showInsufficientBalance && (
        <InsufficientBalanceModal
          isOpen={showInsufficientBalance}
          onClose={() => setShowInsufficientBalance(false)}
          message={balanceErrorMessage}
          requiredAmount={costEstimate ? costEstimate.period_cost * replicas : 0}
          currentBalance={availableBalance || 0}
        />
      )}

      {/* Create SSH Key Modal */}
      {showCreateSSHKeyModal && (
        <CreateSSHKeyModal
          isOpen={showCreateSSHKeyModal}
          onClose={() => {
            setShowCreateSSHKeyModal(false);
            refetchSSHKeys();
          }}
          onKeyCreated={(newKey) => {
            setShowCreateSSHKeyModal(false);
            refetchSSHKeys();
            // Auto-select the newly created SSH key
            if (newKey?.id) {
              setFormData({ ...formData, ssh_key_id: newKey.id });
            }
          }}
        />
      )}

      {/* Create Project Modal */}
      {showCreateProjectModal && (
        <ProjectModal
          isOpen={showCreateProjectModal}
          onClose={() => setShowCreateProjectModal(false)}
          onSubmit={(data) => {
            createProject(data, {
              onSuccess: async () => {
                setShowCreateProjectModal(false);
                // Refetch projects and auto-select the newly created one
                const result = await refetchProjects();
                const projectsList = result.data?.projects || (Array.isArray(result.data) ? result.data : []);
                const foundProject = projectsList.find((p: any) => p.name === data.name);
                if (foundProject) {
                  setFormData({ ...formData, project_id: foundProject.id });
                }
              },
            });
          }}
          isSubmitting={false}
        />
      )}
    </div>
  );
};

export default CreateVMPool;
